/**
 * Public landing page — NIS2 Self-Assessment lead magnet.
 * Route: /landing/nis2 (no authentication required).
 *
 * 4 diagnostic questions -> NIS2 exposure score (0-100) + classification
 * (essential/important/none) + findings + recommendations. Captures lead.
 * Emerald/cyan palette to differentiate from the indigo/fuchsia AI Act page.
 */
import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ShieldCheck, Lock, Sparkles, CheckCircle2, AlertTriangle, ChevronRight, Loader2, Mail, Building2, User as UserIcon, Network } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { toast } from 'sonner';
import useMetaTags from '../../hooks/useMetaTags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ESSENTIAL_SECTORS = [
  { id: 'energy', label: 'Energia' },
  { id: 'transport', label: 'Transportes' },
  { id: 'banking', label: 'Banca' },
  { id: 'financial_market_infra', label: 'Infraestrutura mercado financeiro' },
  { id: 'health', label: 'Saúde' },
  { id: 'drinking_water', label: 'Água potável' },
  { id: 'wastewater', label: 'Águas residuais' },
  { id: 'digital_infra', label: 'Infraestrutura digital (IXPs, DNS, cloud, DCs)' },
  { id: 'ict_service_mgmt', label: 'Gestão de serviços TIC (B2B)' },
  { id: 'public_admin', label: 'Administração pública' },
  { id: 'space', label: 'Espaço' },
];
const IMPORTANT_SECTORS = [
  { id: 'postal', label: 'Serviços postais & correios' },
  { id: 'waste_mgmt', label: 'Gestão de resíduos' },
  { id: 'chemicals', label: 'Químicos' },
  { id: 'food', label: 'Alimentar' },
  { id: 'manufacturing', label: 'Fabrico (dispositivos médicos, elétrica, motorizados)' },
  { id: 'digital_providers', label: 'Prestadores de serviços digitais (marketplaces, motores busca)' },
  { id: 'research', label: 'Investigação' },
];

const LEVEL_UI = {
  critical: { badge: 'bg-red-500/20 text-red-300 border-red-500/40', ring: 'ring-red-500/40', glow: 'shadow-[0_0_60px_-10px_rgba(239,68,68,0.5)]', accent: 'text-red-400', label: 'CRÍTICO' },
  high:     { badge: 'bg-orange-500/20 text-orange-300 border-orange-500/40', ring: 'ring-orange-500/40', glow: 'shadow-[0_0_60px_-10px_rgba(249,115,22,0.5)]', accent: 'text-orange-400', label: 'ALTO' },
  medium:   { badge: 'bg-amber-500/20 text-amber-300 border-amber-500/40', ring: 'ring-amber-500/40', glow: 'shadow-[0_0_60px_-10px_rgba(245,158,11,0.4)]', accent: 'text-amber-400', label: 'MODERADO' },
  low:      { badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', ring: 'ring-emerald-500/40', glow: 'shadow-[0_0_60px_-10px_rgba(16,185,129,0.5)]', accent: 'text-emerald-400', label: 'BAIXO' },
};

export default function Nis2Landing() {
  const [step, setStep] = useState(0); // 0 = hero, 1..4 = questions, 5 = contact, 6 = result
  const [answers, setAnswers] = useState({
    sector: '',
    size: '',
    incident_plan: '',
    reports_cncs: '',
  });
  const [contact, setContact] = useState({ name: '', email: '', company: '', role: '', consent_marketing: false });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [abVariant, setAbVariant] = useState('A');

  // Assign A/B variant on first visit (50/50 sticky per browser)
  useEffect(() => {
    try {
      const KEY = 'nis2ai_ab_variant';
      let v = localStorage.getItem(KEY);
      // Optional URL override for internal QA: ?ab=A or ?ab=B
      const forced = new URLSearchParams(window.location.search).get('ab');
      if (forced && ['A', 'B'].includes(forced.toUpperCase())) {
        v = forced.toUpperCase();
        localStorage.setItem(KEY, v);
      } else if (!v) {
        v = Math.random() < 0.5 ? 'A' : 'B';
        localStorage.setItem(KEY, v);
      }
      setAbVariant(v);
    } catch { /* private-mode: default to A */ }
  }, []);

  useEffect(() => { document.title = 'NIS2AI · Self-Assessment NIS2 & Lei 125/2025 (grátis)'; }, []);
  useMetaTags({
    title: 'Self-Assessment NIS2 & Lei 125/2025 (grátis · 90s) — NIS2AI',
    description: 'Diagnóstico rápido em 4 perguntas: setor Anexo I/II, dimensão, plano incidentes, registo CNCS. Score de exposição + recomendações personalizadas.',
  });

  const totalSteps = 4;
  const progress = useMemo(() => {
    if (step === 0) return 0;
    if (step >= 5) return 100;
    return Math.round((step / totalSteps) * 100);
  }, [step]);

  const canProceed = useMemo(() => {
    switch (step) {
      case 1: return !!answers.sector;
      case 2: return !!answers.size;
      case 3: return !!answers.incident_plan;
      case 4: return !!answers.reports_cncs;
      case 5: return contact.name.trim().length >= 2 && /.+@.+\..+/.test(contact.email) && contact.company.trim().length >= 1;
      default: return true;
    }
  }, [step, answers, contact]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let utm_source;
      try {
        const raw = localStorage.getItem('nis2ai_utm');
        if (raw) utm_source = (JSON.parse(raw).utm_source || undefined);
      } catch { /* noop */ }
      const { data } = await axios.post(`${BACKEND_URL}/api/public/nis2/self-assessment`, {
        ...contact,
        answers,
        ab_variant: abVariant,
        utm_source,
      });
      setResult(data);
      setStep(6);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Não foi possível submeter. Tente novamente em instantes.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#040d14] text-slate-100 relative overflow-hidden">
      {/* Ambient (emerald/cyan/teal — differentiates from AI Act) */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute top-[-20%] left-[-10%] h-[520px] w-[520px] rounded-full bg-emerald-500/20 blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] h-[520px] w-[520px] rounded-full bg-cyan-500/20 blur-[140px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[300px] w-[900px] rounded-full bg-teal-500/10 blur-[100px]" />
      </div>
      <div className="pointer-events-none absolute inset-0 [background:radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.05)_1px,transparent_0)] [background-size:32px_32px] opacity-40" />

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 border-b border-white/5">
        <Link to="/landing/nis2" className="flex items-center gap-2 font-semibold tracking-tight" data-testid="landing-nis2-logo">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <span className="text-lg">NIS2<span className="text-emerald-400">AI</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/landing/ai-act" className="text-sm text-slate-400 hover:text-white transition-colors hidden md:inline" data-testid="landing-nis2-crosslink-aiact">
            Também para AI Act →
          </Link>
          <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="landing-nis2-login-link">Entrar</Link>
          <Button
            onClick={() => setStep(1)}
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-full font-medium"
            data-testid="landing-nis2-cta-nav"
          >
            Iniciar diagnóstico <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </nav>

      <main className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {step === 0 && <Hero onStart={() => setStep(1)} variant={abVariant} />}
        {step >= 1 && step <= 4 && (
          <div className="max-w-3xl mx-auto" data-testid="nis2-assessment-panel">
            <ProgressBar progress={progress} step={step} total={totalSteps} />
            {step === 1 && <Q1Sector value={answers.sector} onChange={(v) => setAnswers({ ...answers, sector: v })} />}
            {step === 2 && <Q2Size value={answers.size} onChange={(v) => setAnswers({ ...answers, size: v })} />}
            {step === 3 && <Q3Plan value={answers.incident_plan} onChange={(v) => setAnswers({ ...answers, incident_plan: v })} />}
            {step === 4 && <Q4Cncs value={answers.reports_cncs} onChange={(v) => setAnswers({ ...answers, reports_cncs: v })} />}
            <StepNav
              onBack={() => setStep(step - 1)}
              onNext={() => setStep(step + 1)}
              canProceed={canProceed}
              nextLabel={step === 4 ? 'Ver resultado →' : 'Continuar'}
              nextTestId={`nis2-next-${step}`}
              backTestId={`nis2-back-${step}`}
            />
          </div>
        )}
        {step === 5 && (
          <ContactForm
            contact={contact}
            setContact={setContact}
            onBack={() => setStep(4)}
            onSubmit={handleSubmit}
            submitting={submitting}
            canProceed={canProceed}
          />
        )}
        {step === 6 && result && (
          <ResultView result={result} onRestart={() => { setStep(0); setResult(null); setAnswers({ sector: '', size: '', incident_plan: '', reports_cncs: '' }); setContact({ name: '', email: '', company: '', role: '', consent_marketing: false }); }} />
        )}
      </main>

      <footer className="relative z-10 border-t border-white/5 py-8 px-6 text-center text-xs text-slate-500">
        <p>
          © {new Date().getFullYear()} NIS2AI · Plataforma multi-tenant de conformidade NIS2 &amp; EU AI Act ·
          {' '}<Link to="/trust" className="hover:text-white underline underline-offset-2" data-testid="nis2-footer-trust-link">Trust Center</Link>
          {' · '}
          <Link to="/insights" className="hover:text-white underline underline-offset-2" data-testid="nis2-footer-insights-link">Insights</Link>
          {' · '}
          <Link to="/login" className="hover:text-white underline underline-offset-2">Entrar</Link>
        </p>
        <p className="mt-1 text-[10px] text-slate-600">O diagnóstico é indicativo, não substitui aconselhamento jurídico especializado.</p>
      </footer>
    </div>
  );
}

// ============================================================================
// Hero
// ============================================================================
function Hero({ onStart, variant = 'A' }) {
  // A/B variants — B is a more benefit-driven, urgent framing
  const isB = variant === 'B';
  const badge = isB
    ? 'Coimas até 10 M€ · 90s para saber a sua exposição'
    : 'Lei 125/2025 em vigor · diagnóstico grátis em 90 segundos';
  const headlineTop = isB ? 'Evite coimas até' : 'A vossa organização está em';
  const headlineBottom = isB ? '10 M€ com a NIS2' : 'conformidade NIS2?';
  const subline = isB
    ? '90 segundos, 4 perguntas. Descubra se a sua organização é entidade essencial (Anexo I) ou importante (Anexo II) da Lei 125/2025 — e receba um plano com prioridades para os próximos 30 dias.'
    : '4 perguntas rápidas e descobre se és uma entidade essencial (Anexo I) ou importante (Anexo II) da Diretiva UE 2022/2555 e Lei portuguesa 125/2025 — com um plano personalizado para fechar os gaps.';
  const ctaLabel = isB ? 'Ver a minha exposição NIS2' : 'Iniciar diagnóstico NIS2';
  return (
    <div className="text-center max-w-3xl mx-auto" data-testid="landing-nis2-hero" data-ab-variant={variant}>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-xs text-slate-300 mb-8">
        <Sparkles className="h-3.5 w-3.5 text-emerald-400" />
        {badge}
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
        {headlineTop}
        <span className="block bg-gradient-to-r from-emerald-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent"> {headlineBottom}</span>
      </h1>
      <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
        {isB ? subline : (<>4 perguntas rápidas e descobre se és uma <b className="text-slate-200">entidade essencial</b> (Anexo I) ou <b className="text-slate-200">importante</b> (Anexo II) da Diretiva UE 2022/2555 e Lei portuguesa 125/2025 — com um plano personalizado para fechar os gaps.</>)}
      </p>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          onClick={onStart}
          size="lg"
          className="bg-white text-slate-900 hover:bg-slate-100 rounded-full font-semibold px-8 h-12 text-base"
          data-testid="landing-nis2-start-btn"
        >
          {ctaLabel} <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <a href="#how" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1">
          O que é a NIS2 <ChevronRight className="h-3.5 w-3.5" />
        </a>
      </div>

      <div id="how" className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
        {[
          { icon: Lock, title: 'Coimas até 10 M€ ou 2%', body: 'Entidades essenciais expostas a coimas administrativas equivalentes ao RGPD. Entidades importantes até 7 M€ ou 1,4% do volume global.' },
          { icon: Network, title: '25 requisitos Art. 21.º', body: 'Gestão de riscos, cadeia de abastecimento, criptografia, MFA, backups, formação, políticas de acesso, testes de segurança.' },
          { icon: ShieldCheck, title: 'Timings 24h / 72h / 30d', body: 'Notificação preliminar em 24h ao CNCS, notificação formal em 72h e relatório final em 30 dias (Art. 23.º).' },
        ].map((c, i) => (
          <div key={i} className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md hover:bg-white/[0.04] transition-colors">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center mb-4">
              <c.icon className="h-5 w-5 text-emerald-400" />
            </div>
            <h3 className="font-semibold text-slate-100">{c.title}</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Progress + StepNav (shared components)
// ============================================================================
function ProgressBar({ progress, step, total }) {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
        <span>Pergunta {step} de {total}</span>
        <span>{progress}%</span>
      </div>
      <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function StepNav({ onBack, onNext, canProceed, nextLabel, nextTestId, backTestId }) {
  return (
    <div className="mt-10 flex items-center justify-between">
      <button
        onClick={onBack}
        className="text-sm text-slate-400 hover:text-white transition-colors"
        data-testid={backTestId}
      >
        ← Voltar
      </button>
      <Button
        onClick={onNext}
        disabled={!canProceed}
        className="bg-white text-slate-900 hover:bg-slate-100 rounded-full font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={nextTestId}
      >
        {nextLabel} <ArrowRight className="h-4 w-4 ml-1.5" />
      </Button>
    </div>
  );
}

// ============================================================================
// Questions
// ============================================================================
function QuestionShell({ title, subtitle, children }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight leading-tight">{title}</h2>
        {subtitle && <p className="mt-2 text-sm text-slate-400">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function ChoiceRadio({ value, onChange, options, testIdPrefix, accent = 'emerald' }) {
  const ringClass = accent === 'cyan'
    ? 'border-cyan-400 bg-cyan-500/10 shadow-[0_0_40px_-15px_rgba(34,211,238,0.5)]'
    : 'border-emerald-400 bg-emerald-500/10 shadow-[0_0_40px_-15px_rgba(52,211,153,0.5)]';
  const dotClass = accent === 'cyan' ? 'text-cyan-400' : 'text-emerald-400';
  return (
    <RadioGroup value={value} onValueChange={onChange} className="space-y-2">
      {options.map(opt => (
        <label
          key={opt.value}
          htmlFor={`${testIdPrefix}-${opt.value}`}
          className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
            value === opt.value ? ringClass : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20'
          }`}
        >
          <RadioGroupItem value={opt.value} id={`${testIdPrefix}-${opt.value}`} data-testid={`${testIdPrefix}-${opt.value}`} className={`mt-0.5 border-slate-500 ${dotClass}`} />
          <div className="flex-1">
            <p className="font-medium text-slate-100">{opt.label}</p>
            {opt.hint && <p className="text-xs text-slate-400 mt-0.5">{opt.hint}</p>}
          </div>
        </label>
      ))}
    </RadioGroup>
  );
}

function Q1Sector({ value, onChange }) {
  return (
    <QuestionShell
      title="Em que setor opera a vossa organização?"
      subtitle="A Diretiva NIS2 lista setores essenciais (Anexo I) e importantes (Anexo II) — a classificação determina o nível de supervisão e coimas aplicáveis."
    >
      <div className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2">Entidades essenciais (Anexo I)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {ESSENTIAL_SECTORS.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => onChange(s.id)}
                className={`text-left px-3 py-2 rounded-lg border text-sm transition-all ${
                  value === s.id
                    ? 'border-emerald-400 bg-emerald-500/15 text-white'
                    : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-slate-300'
                }`}
                data-testid={`q1-sector-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-cyan-400 font-semibold mb-2">Entidades importantes (Anexo II)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {IMPORTANT_SECTORS.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => onChange(s.id)}
                className={`text-left px-3 py-2 rounded-lg border text-sm transition-all ${
                  value === s.id
                    ? 'border-cyan-400 bg-cyan-500/15 text-white'
                    : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-slate-300'
                }`}
                data-testid={`q1-sector-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <button
            type="button"
            onClick={() => onChange('none')}
            className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${
              value === 'none'
                ? 'border-slate-400 bg-slate-500/15 text-white'
                : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-slate-400'
            }`}
            data-testid="q1-sector-none"
          >
            Nenhum dos anteriores — sem enquadramento setorial direto
          </button>
        </div>
      </div>
    </QuestionShell>
  );
}

function Q2Size({ value, onChange }) {
  return (
    <QuestionShell
      title="Qual a dimensão da vossa organização?"
      subtitle="A NIS2 usa critério dimensional: ≥50 colaboradores OU ≥10 M€ de volume de negócios coloca a organização no âmbito obrigatório (Art. 2.º)."
    >
      <ChoiceRadio
        value={value}
        onChange={onChange}
        testIdPrefix="q2-size"
        accent="emerald"
        options={[
          { value: 'large', label: 'Grande (≥250 colaboradores ou ≥50 M€ vol. negócios)', hint: 'Âmbito obrigatório NIS2 se em setor coberto' },
          { value: 'medium', label: 'Média (50-249 colaboradores ou 10-50 M€)', hint: 'Âmbito obrigatório NIS2 se em setor coberto' },
          { value: 'small', label: 'Pequena (10-49 colaboradores ou 2-10 M€)', hint: 'Normalmente fora do âmbito obrigatório' },
          { value: 'micro', label: 'Micro (<10 colaboradores ou <2 M€)', hint: 'Fora do âmbito exceto casos específicos (DNS, TLD, digital)' },
        ]}
      />
    </QuestionShell>
  );
}

function Q3Plan({ value, onChange }) {
  return (
    <QuestionShell
      title="Têm um plano formal de gestão de incidentes de cibersegurança?"
      subtitle="Art. 21.º 2 b) — obrigação de medidas técnicas e organizativas, incluindo tratamento de incidentes com fluxos 24h/72h/30d."
    >
      <ChoiceRadio
        value={value}
        onChange={onChange}
        testIdPrefix="q3-plan"
        accent="cyan"
        options={[
          { value: 'yes', label: 'Sim — plano documentado e testado', hint: 'Playbooks, RACI, tabletop exercises anuais' },
          { value: 'partial', label: 'Parcial', hint: 'Existem procedimentos, mas não formalizados ou testados' },
          { value: 'no', label: 'Não', hint: 'Sem plano documentado' },
        ]}
      />
    </QuestionShell>
  );
}

function Q4Cncs({ value, onChange }) {
  return (
    <QuestionShell
      title="A organização reporta / está registada junto do CNCS?"
      subtitle="Centro Nacional de Cibersegurança — autoridade competente para a Lei 125/2025 (transposição PT da NIS2). Registo + SPOC (Art. 22.º) são obrigatórios."
    >
      <ChoiceRadio
        value={value}
        onChange={onChange}
        testIdPrefix="q4-cncs"
        accent="emerald"
        options={[
          { value: 'yes', label: 'Sim — registados e com SPOC nomeado', hint: 'Ponto de Contacto Único comunicado ao CNCS' },
          { value: 'planned', label: 'Em processo de registo', hint: 'Passos iniciados nos últimos 6 meses' },
          { value: 'unsure', label: 'Não sabemos se precisamos', hint: 'Precisamos de confirmar obrigação' },
          { value: 'no', label: 'Não — sem registo nem SPOC', hint: 'Sem qualquer contacto com o CNCS' },
        ]}
      />
    </QuestionShell>
  );
}

// ============================================================================
// Contact Form
// ============================================================================
function ContactForm({ contact, setContact, onBack, onSubmit, submitting, canProceed }) {
  return (
    <div className="max-w-2xl mx-auto" data-testid="nis2-contact-form">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-xs text-emerald-300 mb-6">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Respostas registadas — quase pronto
        </div>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">Onde enviamos o vosso diagnóstico NIS2?</h2>
        <p className="mt-3 text-sm text-slate-400 max-w-lg mx-auto">
          Vamos calcular a classificação (essencial/importante), score de exposição e recomendações personalizadas. Sem spam.
        </p>
      </div>

      <div className="space-y-4 bg-white/[0.02] border border-white/10 rounded-2xl p-6 backdrop-blur-md">
        <div>
          <Label htmlFor="nis2-name" className="text-slate-300 text-xs uppercase tracking-widest">Nome</Label>
          <div className="relative mt-1.5">
            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="nis2-name"
              value={contact.name}
              onChange={e => setContact({ ...contact, name: e.target.value })}
              placeholder="Maria Silva"
              className="pl-9 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-emerald-400"
              data-testid="nis2-contact-name"
              autoFocus
            />
          </div>
        </div>
        <div>
          <Label htmlFor="nis2-email" className="text-slate-300 text-xs uppercase tracking-widest">Email profissional</Label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="nis2-email"
              type="email"
              value={contact.email}
              onChange={e => setContact({ ...contact, email: e.target.value })}
              placeholder="maria@empresa.pt"
              className="pl-9 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-emerald-400"
              data-testid="nis2-contact-email"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="nis2-company" className="text-slate-300 text-xs uppercase tracking-widest">Empresa</Label>
            <div className="relative mt-1.5">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                id="nis2-company"
                value={contact.company}
                onChange={e => setContact({ ...contact, company: e.target.value })}
                placeholder="Acme Lda"
                className="pl-9 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-emerald-400"
                data-testid="nis2-contact-company"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="nis2-role" className="text-slate-300 text-xs uppercase tracking-widest">Cargo (opcional)</Label>
            <Input
              id="nis2-role"
              value={contact.role}
              onChange={e => setContact({ ...contact, role: e.target.value })}
              placeholder="CISO, DPO, CTO…"
              className="mt-1.5 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-emerald-400"
              data-testid="nis2-contact-role"
            />
          </div>
        </div>
        <label className="flex items-start gap-2 pt-2 cursor-pointer">
          <Checkbox
            checked={contact.consent_marketing}
            onCheckedChange={(v) => setContact({ ...contact, consent_marketing: !!v })}
            className="mt-0.5 border-slate-500 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
            data-testid="nis2-contact-consent"
          />
          <span className="text-xs text-slate-400 leading-relaxed">
            Autorizo receber updates ocasionais sobre NIS2 e EU AI Act (posso cancelar a qualquer momento).
          </span>
        </label>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={onBack}
          className="text-sm text-slate-400 hover:text-white transition-colors"
          data-testid="nis2-contact-back-btn"
        >
          ← Voltar
        </button>
        <Button
          onClick={onSubmit}
          disabled={!canProceed || submitting}
          className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white rounded-full font-semibold px-8 h-11 disabled:opacity-40"
          data-testid="nis2-contact-submit-btn"
        >
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A calcular…</> : <>Ver o meu diagnóstico <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Result
// ============================================================================
function ResultView({ result, onRestart }) {
  const ui = LEVEL_UI[result.level] || LEVEL_UI.medium;
  return (
    <div className="max-w-3xl mx-auto" data-testid="nis2-assessment-result">
      <div className="text-center mb-10">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs mb-6 ${ui.badge}`} data-testid="nis2-result-level-badge">
          <AlertTriangle className="h-3.5 w-3.5" />
          Nível de exposição · {ui.label}
        </div>
        <div className={`inline-flex items-center justify-center h-40 w-40 rounded-full bg-white/[0.03] border border-white/10 ${ui.glow}`}>
          <div className="text-center">
            <div className={`text-5xl font-bold ${ui.accent}`} data-testid="nis2-result-score">{result.score}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">de 100</div>
          </div>
        </div>
        <h2 className="mt-8 text-2xl md:text-3xl font-semibold tracking-tight max-w-xl mx-auto leading-snug">
          {result.headline}
        </h2>
      </div>

      {result.findings?.length > 0 && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-4 backdrop-blur-md">
          <h3 className="font-semibold text-sm uppercase tracking-widest text-slate-400 mb-4">Principais achados</h3>
          <ul className="space-y-3">
            {result.findings.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed" data-testid={`nis2-result-finding-${i}`}>
                <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${ui.accent}`} />
                <span dangerouslySetInnerHTML={{ __html: f.replace(/\*\*(.+?)\*\*/g, '<b class="text-slate-100">$1</b>') }} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.recommendations?.length > 0 && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-8 backdrop-blur-md">
          <h3 className="font-semibold text-sm uppercase tracking-widest text-slate-400 mb-4">Próximos passos recomendados</h3>
          <ul className="space-y-3">
            {result.recommendations.map((r, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-200 leading-relaxed" data-testid={`nis2-result-rec-${i}`}>
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-400" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-gradient-to-br from-emerald-500/10 via-cyan-500/10 to-teal-500/10 border border-white/10 rounded-2xl p-8 text-center backdrop-blur-md">
        <Sparkles className="h-8 w-8 mx-auto text-emerald-400 mb-3" />
        <h3 className="text-xl font-semibold">Pronto para acelerar a conformidade NIS2?</h3>
        <p className="mt-2 text-sm text-slate-300 max-w-lg mx-auto">
          O NIS2AI cobre os 25 requisitos NIS2 com workflows 24h/72h, gestão de vulnerabilidades com webhooks Nessus/Qualys, e PDFs regulatórios prontos a submeter ao CNCS.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={`mailto:contacto@nis2ai.pt?subject=Demo%20NIS2AI%20-%20NIS2%20(score%20${result.score})&body=Ol%C3%A1%2C%0A%0AFa%C3%A7o%20seguimento%20ao%20diagn%C3%B3stico%20NIS2%20com%20score%20${result.score}/100%20(${ui.label}).%20Gostaria%20de%20agendar%20uma%20demo.%0A%0AObrigado.`}
            className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 rounded-full font-semibold h-11 px-6 text-sm transition-colors"
            data-testid="nis2-result-book-demo-btn"
          >
            Agendar demo de 20 min <ArrowRight className="h-4 w-4" />
          </a>
          <Link to="/landing/ai-act" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="nis2-result-crosslink-aiact">
            Também tenho IA · avaliar AI Act →
          </Link>
        </div>
      </div>

      <div className="mt-10 text-center">
        <button
          onClick={onRestart}
          className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="nis2-result-restart-btn"
        >
          ↺ Refazer diagnóstico
        </button>
      </div>
    </div>
  );
}
